import React, { useCallback, useMemo, useState } from "react";
import {
  Checkbox,
  FormControl,
  FormHelperText,
  Select as MaterialSelect,
  MenuItem,
  MenuProps,
  Typography,
  ListItemIcon,
} from "@material-ui/core";
import classNames from "classnames";
import { Chevron } from "../../icons";
import { ISelectProps } from "./types";
import { useSelectStyles } from "./styles";

const NO_VALUE = "none";

const Select = (props: ISelectProps) => {
  const {
    value,
    label,
    options,
    onChange,
    fullWidth,
    error,
    LabelComponent,
    required,
    size = "lg",
    disabled,
    multiple,
    placeholder,
    isOpen,
    inputTestId = "selector-input",
    ...rest
  } = props;
  const [input, setInput] = useState<unknown>(null);
  const [open, setOpen] = useState<boolean>(false);
  const classes = useSelectStyles({
    ...props,
    open,
    width: (input as HTMLSelectElement)?.parentElement?.clientWidth,
  });

  const handleChange = (
    event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>,
  ) => {
    onChange(event.target.value as string & string[], event.target.name);
  };

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const containerClasses = classNames(classes.container, classes[size], {
    [classes.disabled]: disabled,
    [classes.error]: error && !open,
    [classes.value]: value,
  });

  const SelectMenuProps = useMemo(
    (): Partial<MenuProps> => ({
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
      transformOrigin: {
        vertical: 0,
        horizontal: "left",
      },
      getContentAnchorEl: null,
      PopoverClasses: {
        root: classes.popover,
        paper: classes.popoverPaper,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // The Select material component doesn't accept placeholder. A workaround is
  // setting a default value when the value is empty and hide the item when the
  // popup is open https://github.com/mui-org/material-ui/issues/11069
  const getValue = useCallback((): string | string[] => {
    if (placeholder && !multiple && !value) return NO_VALUE;

    return value;
  }, [placeholder, multiple, value]);

  const parseMultipleValue = useCallback(
    (selected: unknown): string | undefined =>
      options
        .reduce((acc: string[], option): string[] => {
          if ((selected as string[]).includes(option.value)) {
            acc.push(option.label);
          }

          return acc;
        }, [])
        .join(", "),
    [options],
  );

  return (
    <FormControl variant="outlined" fullWidth={fullWidth}>
      <Typography component="div" className={classes.label}>
        {label && required ? `${label}*` : label}
        {LabelComponent && <LabelComponent />}
      </Typography>
      <MaterialSelect
        inputProps={{
          "data-testid": inputTestId,
        }}
        {...rest}
        ref={(ref) => {
          setInput(ref);
        }}
        value={getValue()}
        multiple={multiple}
        disabled={disabled}
        required={required}
        onChange={handleChange}
        onOpen={handleOpen}
        onClose={handleClose}
        MenuProps={SelectMenuProps}
        className={containerClasses}
        IconComponent={Chevron}
        open={isOpen}
        renderValue={multiple ? parseMultipleValue : undefined}
      >
        {placeholder && !open && (
          <MenuItem disabled value={NO_VALUE}>
            {placeholder}
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem
            key={option.value}
            disabled={option.disabled}
            value={option.value}
          >
            {multiple && <Checkbox checked={value?.includes(option.value)} />}
            {option.leftAdornment && (
              <ListItemIcon>{option.leftAdornment}</ListItemIcon>
            )}
            <Typography
              variant="inherit"
              className={classes.selectText}
              component="div"
            >
              {option.label}
            </Typography>
          </MenuItem>
        ))}
      </MaterialSelect>
      {error && (
        <FormHelperText className={classes.errorDescription} error={!!error}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default Select;
